import {
  type PropsWithChildren,
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react'

import { MantineProvider } from '@mantine/core'

import { persistPrimaryColor } from '@/lib/api/user-preferences/user-preferences.client'
import { cssVariablesResolver, theme } from '@/lib/theme'
import { DEFAULT_COLOR_SCHEME, DEFAULT_PRIMARY_COLOR } from '@/lib/theme/constants'
import { ThemePreferences } from '@/types/platform'

import { createColorSchemeManager } from './color-scheme-manager'

type PrimaryColorContextType = {
  primaryColor: string
  changePrimaryColor: (color: string) => void
}

const PrimaryColorContext = createContext<PrimaryColorContextType>({
  primaryColor: DEFAULT_PRIMARY_COLOR,
  changePrimaryColor: () => {},
})

type PrimaryColorProviderProps = PropsWithChildren<{
  initialTheme?: ThemePreferences
}>

const PrimaryColorProvider = (props: PrimaryColorProviderProps) => {
  const {
    children,
    initialTheme = {
      primaryColor: DEFAULT_PRIMARY_COLOR,
      colorScheme: DEFAULT_COLOR_SCHEME,
    },
  } = props
  const [primaryColor, setPrimaryColor] = useState(initialTheme.primaryColor)

  // The manager owns the scheme once mounted, so it is created only once.
  const [colorSchemeManager] = useState(() =>
    createColorSchemeManager(initialTheme.colorScheme),
  )

  const changePrimaryColor = useCallback((color: string) => {
    setPrimaryColor(color)
    persistPrimaryColor(color)
  }, [])

  const value = useMemo(
    () => ({ primaryColor, changePrimaryColor }),
    [primaryColor, changePrimaryColor],
  )

  return (
    <PrimaryColorContext.Provider value={value}>
      <MantineProvider
        cssVariablesResolver={cssVariablesResolver}
        colorSchemeManager={colorSchemeManager}
        defaultColorScheme={initialTheme.colorScheme}
        theme={{ ...theme, primaryColor }}
      >
        {children}
      </MantineProvider>
    </PrimaryColorContext.Provider>
  )
}

export const usePrimaryColor = () => {
  const context = useContext(PrimaryColorContext)
  if (!context) {
    throw new Error('usePrimaryColor must be used within a PrimaryColorProvider')
  }
  return context
}

export default PrimaryColorProvider
